/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import actions from '@src/redux/actions';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { Fade, MenuItem, Paper, Popper } from '@mui/material';
import {
  FlagRounded,
  PlayCircleFilledRounded,
  ReplayRounded,
  WarningRounded,
} from '@mui/icons-material';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import apis from '@src/apis';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { downloadFileUsingUrl, createFileFromText } from '@src/utils/file';
import { trackAudioDownloaded, trackAudioPlayed } from '@src/services/moengage';
import { MOE_MODULE, MOE_SURFACE } from '@src/constants/moengage';
import { MAX_AUDIO_RETENTION_PERIOD } from '@src/constants';
import { DOWNLOAD_QUOTA_EXCEEDED_CODE } from '@src/constants/tts';
import { REQUEST_STATUS } from '@src/constants/voice';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { COLOR } from '@src/styles/color';
import {
  StyledIconButton,
  StyledActionTooltip,
} from '../../TTSNew/NewRequest/index.style';

let globalAudioInstance = null;
let globalAudioStopCallback = null;

const Action = ({
  request,
  onOpenErrorReport,
  playable,
  isDisabledErrorReport = false,
  isSTT = false,
  onRefresh,
}) => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { t } = useTranslation();
  const {
    id: requestId,
    title,
    characters,
    voice,
    audioLink,
    retentionPeriod = MAX_AUDIO_RETENTION_PERIOD,
    createdAt,
    status,
    type,
    seconds,
    sentencesVoiceCode,
  } = request;

  const dispatch = useDispatch();
  const { displayStatus } = useRequestAnimationStatus(request);
  const [isPlaying, setIsPlaying] = useState(false);
  const [localAudioRef] = useState(() => new Audio());
  const isMountedRef = useRef(true);

  useEffect(() => {
    const audio = localAudioRef;

    const handleEnded = () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
      }
      if (globalAudioInstance === audio) {
        globalAudioInstance = null;
        globalAudioStopCallback = null;
      }
    };

    const handleError = () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'playAudioError',
          }),
        );
      }
      if (globalAudioInstance === audio) {
        globalAudioInstance = null;
        globalAudioStopCallback = null;
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      isMountedRef.current = false;
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
      if (globalAudioInstance === audio) {
        globalAudioInstance = null;
        globalAudioStopCallback = null;
      }
    };
  }, [localAudioRef, dispatch]);

  const handleOpenErrorReport = (e) => {
    e.stopPropagation();
    onOpenErrorReport({
      id: requestId,
      title,
      characters,
      voice,
      type,
      seconds,
      sentencesVoiceCode,
    });
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();

    if (!audioLink) return;

    if (isPlaying) {
      localAudioRef.pause();
      setIsPlaying(false);
      if (globalAudioInstance === localAudioRef) {
        globalAudioInstance = null;
        globalAudioStopCallback = null;
      }
    } else {
      // Stop any currently playing audio from other components
      if (globalAudioInstance && globalAudioInstance !== localAudioRef) {
        globalAudioInstance.pause();
        if (globalAudioStopCallback) {
          globalAudioStopCallback();
        }
      }

      // Pause current audio if it's playing
      if (!localAudioRef.paused) {
        localAudioRef.pause();
      }

      // Set new audio source and play
      localAudioRef.src = audioLink;
      localAudioRef.play().catch(() => {
        if (isMountedRef.current) {
          setIsPlaying(false);
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: 'playAudioError',
            }),
          );
        }
      });
      setIsPlaying(true);
      trackAudioPlayed({
        requestId,
        module: MOE_MODULE.API,
        surface: MOE_SURFACE.REQUEST_LIST,
        durationSeconds: seconds ? Math.round(seconds) : undefined,
        voiceCode: voice?.code,
      });

      // Set this as the global playing audio
      globalAudioInstance = localAudioRef;
      globalAudioStopCallback = () => {
        if (isMountedRef.current) {
          setIsPlaying(false);
        }
      };
    }
  };

  const handleDownloadTranscript = async (e) => {
    e.stopPropagation();
    const res = await apis.stt.getSTTTranscriptDetail({
      transcriptId: requestId,
    });
    if (!res?.transcript) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'downloadError' }),
      );
      return;
    }
    const file = createFileFromText(`${requestId}.txt`, res.transcript);
    const blobUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${requestId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadAudio = async (e) => {
    e.stopPropagation();

    const res = await apis.requests.getPresignedAudioUrl(requestId);
    if (res?.status) {
      const audioUrl = res.result?.audio;
      trackAudioDownloaded({
        requestId,
        module: MOE_MODULE.API,
        surface: MOE_SURFACE.REQUEST_LIST,
      });
      downloadFileUsingUrl(audioUrl);
      return;
    }

    if (res?.errorCode === DOWNLOAD_QUOTA_EXCEEDED_CODE) {
      dispatch(actions.user.updateShowBlockDownloadDialog(true));
      return;
    }

    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'downloadError',
        value: title,
      }),
    );
  };

  const audioExpired = moment().isAfter(
    moment(createdAt).add(retentionPeriod, 'days'),
  );
  const isDisabledAudio =
    !audioLink || audioExpired || displayStatus !== REQUEST_STATUS.SUCCESS;

  const getTitleActionTooltip = (action) => {
    // Request failed and processing
    if (status !== REQUEST_STATUS.SUCCESS) return t('');
    // Audio expired
    if (audioExpired) return t('notiAudioExpired', { retentionPeriod });

    return t(action);
  };

  const [anchorStatusRequest, setAnchorStatusRequest] = useState(null);
  const [openPopperStatusRequest, setOpenPopperStatusRequest] = useState(false);
  const refClickOutSide = useClickOutside(() => {
    setOpenPopperStatusRequest(false);
  });

  const handleRedeliverCallback = async (e) => {
    e.stopPropagation();
    setOpenPopperStatusRequest(false);
    const res = await apis.stt.redeliverSTTCallback({
      transcriptId: requestId,
    });
    if (!res || !res?.enqueuedOutboxId) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'sttRedeliverCallbackError',
        }),
      );
      return;
    }
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'sttRedeliverCallbackSuccess',
      }),
    );
    // Server enqueues an outbox row; the dispatcher cron tick (10s) picks it
    // up and POSTs to the customer URL. Wait 12s (10s tick + 2s buffer) before
    // refreshing so the row reload reflects the new clientSttCallback* fields.
    setTimeout(() => onRefresh?.(), 12000);
  };

  return (
    <div className="action-cell">
      {isSTT && request.hasWarning && (
        <StyledActionTooltip
          title={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              <span>{t('sttCallbackFailed')}</span>
              <span>
                {t('sttCallbackErrorCode', {
                  code: request.callbackStatusCode,
                })}
              </span>
              <span>
                {t('sttCallbackTime', {
                  time: request.callbackAt
                    ? moment(request.callbackAt).format('DD/MM/YYYY - HH:mm:ss')
                    : '—',
                })}
              </span>
            </div>
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: { sx: { maxWidth: 'none !important' } },
          }}
        >
          <span>
            <StyledIconButton
              height="40px"
              width="40px"
              padding="12px"
              isDisableHover
            >
              <WarningRounded sx={{ fontSize: '24px', color: '#FF3B30' }} />
            </StyledIconButton>
          </span>
        </StyledActionTooltip>
      )}
      {displayStatus === 'SUCCESS' && (
        <>
          {playable && !isDubbingRoute && (
            <StyledActionTooltip
              data-id="play-req-audio"
              data-request-id={request.id}
              title={getTitleActionTooltip('playAudio')}
              arrow
              placement="top"
              disabled={isDisabledAudio}
            >
              <span>
                <StyledIconButton
                  onClick={handleChangePlayAudio}
                  disabled={isDisabledAudio}
                  height="40px"
                  width="40px"
                  padding="12px"
                >
                  {isPlaying ? (
                    <PauseCircleFilledRoundedIcon
                      style={{ width: '24px' }}
                      className="icon"
                    />
                  ) : (
                    <PlayCircleFilledRounded
                      style={{
                        fontSize: '24px',
                      }}
                      className={
                        isDisabledAudio ? 'icon icon-disabled' : 'icon'
                      }
                    />
                  )}
                </StyledIconButton>
              </span>
            </StyledActionTooltip>
          )}
          <StyledActionTooltip
            data-id="download-req-audio"
            data-request-id={request.id}
            title={getTitleActionTooltip('download')}
            arrow
            placement="top"
            disabled={!isSTT && isDisabledAudio}
          >
            <span>
              <StyledIconButton
                onClick={isSTT ? handleDownloadTranscript : handleDownloadAudio}
                disabled={!isSTT && isDisabledAudio}
                height="40px"
                width="40px"
                padding="12px"
              >
                <DownloadRoundedIcon
                  style={{ fontSize: '24px' }}
                  className={
                    !isSTT && isDisabledAudio ? 'icon icon-disabled' : 'icon'
                  }
                />
              </StyledIconButton>
            </span>
          </StyledActionTooltip>
        </>
      )}

      <div ref={refClickOutSide}>
        <StyledIconButton
          data-id="view-other-req-actions"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorStatusRequest(e.currentTarget);
            setOpenPopperStatusRequest((prev) => !prev);
          }}
          height="40px"
          width="40px"
          padding="12px"
        >
          <MoreVertOutlinedIcon
            className="icon-morevert"
            color="rgba(30, 22, 78, 0.64)"
          />

          <Popper
            transition
            style={{ zIndex: 1000, width: '180px', color: '#1E164E' }}
            anchorEl={anchorStatusRequest}
            open={openPopperStatusRequest}
            placement="bottom-end"
          >
            {({ TransitionProps }) => (
              <Fade
                style={{ padding: '4px 0px' }}
                {...TransitionProps}
                timeout={350}
              >
                <Paper>
                  {isSTT && request.hasWarning && (
                    <MenuItem
                      key="handleRedeliverCallback"
                      style={{
                        color: '#1E164E',
                        fontSize: '14px',
                        height: '40px',
                        boxSizing: 'border-box',
                        padding: '10px 16px',
                        fontWeight: 500,
                      }}
                      onClick={handleRedeliverCallback}
                    >
                      <StyledIconButton
                        height="40px"
                        width="40px"
                        padding="12px"
                        isDisableHover
                      >
                        <ReplayRounded
                          style={{ color: COLOR.darkGray, fontSize: '24px' }}
                          className="icon"
                        />
                      </StyledIconButton>
                      <span className="option-title">
                        {t('sttRedeliverCallback')}
                      </span>
                    </MenuItem>
                  )}
                  <MenuItem
                    data-id="report-req"
                    data-request-id={requestId}
                    key="handleOpenErrorReport"
                    style={{
                      color: '#1E164E',
                      fontSize: '14px',
                      height: '40px',
                      boxSizing: 'border-box',
                      padding: '10px 16px',
                      fontWeight: 500,
                    }}
                    onClick={handleOpenErrorReport}
                    disabled={isDisabledErrorReport}
                  >
                    <StyledIconButton
                      height="40px"
                      width="40px"
                      padding="12px"
                      isDisableHover
                      disabled={isDisabledErrorReport}
                    >
                      <FlagRounded
                        style={{ color: '#FF3B30', fontSize: '24px' }}
                        className="icon"
                      />
                    </StyledIconButton>
                    <span className="option-title">{t('errorReport')}</span>
                  </MenuItem>
                </Paper>
              </Fade>
            )}
          </Popper>
        </StyledIconButton>
      </div>
    </div>
  );
};
export default Action;
